/**
 * Shopfloor — tablet hamburger menu
 * ------------------------------------------------------------------
 * The app shell (`.app-frame` / `.app-sidebar`) is rendered by the shared
 * `@yuksalish/ui` package, so this Shopfloor-only module injects the small
 * pieces the drawer needs on top of it:
 *
 *   - a sticky top bar with a hamburger toggle button
 *   - a click-to-close backdrop
 *
 * `tablet.css` then turns the sidebar into an off-canvas drawer and animates
 * it in/out via the `body.shopfloor-menu-open` class. The toggle/backdrop are
 * hidden on landscape/desktop widths by the same stylesheet.
 *
 * The shared shell only exists after login, so a MutationObserver re-injects
 * if the frame appears later (or after a logout → login round trip).
 */

function isMenuOpen(): boolean {
  return document.body.classList.contains('shopfloor-menu-open')
}

function setMenuOpen(open: boolean): void {
  document.body.classList.toggle('shopfloor-menu-open', open)
  const toggle = document.querySelector<HTMLButtonElement>('.shopfloor-menu-toggle')
  toggle?.setAttribute('aria-expanded', String(open))
}

function closeMenu(): void {
  setMenuOpen(false)
}

let keydownWired = false

function bindSidebarClose(frame: HTMLElement): void {
  const sidebar = frame.querySelector<HTMLElement>('.app-sidebar')
  sidebar?.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null
    if (target?.closest('a, button')) {
      closeMenu()
    }
  })
}

function wireGlobal(): void {
  if (keydownWired) return
  keydownWired = true
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeMenu()
  })
}

function injectInto(frame: HTMLElement): boolean {
  if (frame.querySelector('.shopfloor-topbar')) return true

  const backdrop = document.createElement('div')
  backdrop.className = 'shopfloor-menu-backdrop'
  backdrop.setAttribute('aria-hidden', 'true')

  const topbar = document.createElement('div')
  topbar.className = 'shopfloor-topbar'

  const toggle = document.createElement('button')
  toggle.type = 'button'
  toggle.className = 'shopfloor-menu-toggle'
  toggle.setAttribute('aria-label', 'Menu')
  toggle.setAttribute('aria-expanded', 'false')
  toggle.innerHTML =
    '<span class="shopfloor-menu-icon" aria-hidden="true"></span>' +
    '<span class="shopfloor-menu-label">Menu</span>'

  const title = document.createElement('span')
  title.className = 'shopfloor-topbar-title'
  title.textContent = 'Yuksalish ERP'

  // Right-aligned help icon → opens the Help wiki (/help).
  const help = document.createElement('a')
  help.className = 'shopfloor-help-toggle'
  help.href = '/help'
  help.setAttribute('aria-label', 'Yordam')
  help.setAttribute('title', 'Yordam')
  help.innerHTML =
    '<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>'

  topbar.append(toggle, title, help)
  frame.prepend(backdrop)
  frame.prepend(topbar)

  toggle.addEventListener('click', () => setMenuOpen(!isMenuOpen()))
  backdrop.addEventListener('click', closeMenu)
  bindSidebarClose(frame)
  wireGlobal()

  return true
}

/** Idempotent: keeps the drawer chrome in sync with the app shell. */
export function setupTabletMenu(): void {
  if (typeof document === 'undefined') return

  const tryInject = (): boolean => {
    const frame = document.querySelector<HTMLElement>('.app-frame')
    return !!frame && injectInto(frame)
  }

  // The shell may already exist (e.g. a restored session on page refresh).
  tryInject()

  // Persistent observer — deliberately never disconnected. The shell is torn
  // down and re-created on every logout → login round trip, and on the very
  // first sign-in it can be injected into and then replaced within a single
  // render. Watching forever guarantees the top bar reappears whenever a
  // `.app-frame` is (re)created. `injectInto` is a no-op once the top bar is
  // present, so repeated calls are harmless.
  const observer = new MutationObserver(() => {
    tryInject()
  })
  observer.observe(document.getElementById('app') ?? document.body, {
    childList: true,
    subtree: true,
  })

  // Polling safety net for the first sign-in: the shell is rendered, injected
  // into, and sometimes replaced all within the same frame — a mutation
  // callback can observe the discarded frame and the top bar is lost. A cheap
  // idempotent poll guarantees it appears even if a mutation is missed.
  window.setInterval(() => {
    tryInject()
  }, 400)
}
